import { pdf } from '@react-pdf/renderer';
import CotizacionPDF from '../components/pdf/CotizacionPDF';
import { nombreArchivoCotizacion, imageToPngDataURL } from './helpers';
import logoDefault from '../assets/almaquinta.svg';

/**
 * Enriquece el objeto cotizacion con los totales derivados que necesita el
 * PDF: separa opcionales de no opcionales, recalcula subtotal/IGV/total sin
 * incluir opcionales y agrega el bloque de opcionales aparte.
 *
 * Se ejecuta SIEMPRE dentro de generatePdfCotizacion — asi todos los call
 * sites (form, detalle, listado, mis-cotizaciones, cliente-cotizaciones)
 * obtienen la nueva estructura sin tener que replicar el calculo.
 *
 * Preserva los campos que ya vinieran en `cotizacion` (permite que el form,
 * que los recalcula en vivo con variaciones, siga ganando prioridad).
 */
function enrichCotizacionParaPdf(cotizacion, servicios, extras, configuracion) {
    const servs = Array.isArray(servicios) ? servicios : [];
    const exts = Array.isArray(extras) ? extras : [];

    const igvRate = parseFloat(cotizacion.igv_rate)
        || parseFloat(configuracion?.igv_rate)
        || 0.18;
    const aplicaIgv = cotizacion.incluye_igv !== false && cotizacion.incluye_igv !== 0;

    const subtotalServicios = servs
        .filter(s => !s.es_opcional)
        .reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);

    const subtotalExtras = exts.reduce((acc, e) => acc + (parseFloat(e.costo) || 0), 0);
    // IMPORTANTE: los extras NO forman parte de la base del IGV — son un cargo
    // fijo que se suma DESPUES del IGV. `subtotal` refleja solo servicios (base
    // del IGV), y el total final agrega los extras al final. Debe coincidir con
    // el calculo del form (computed en CotizacionForm.js).
    const subtotal = subtotalServicios;
    const igv = aplicaIgv ? subtotal * igvRate : 0;
    const total = subtotal + igv + subtotalExtras;

    const subtotalOpcionales = servs
        .filter(s => !!s.es_opcional)
        .reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);
    const igvOpcionales = aplicaIgv ? subtotalOpcionales * igvRate : 0;
    const totalOpcionales = subtotalOpcionales + igvOpcionales;

    // El enrichment SIEMPRE gana: recalcula desde s.total (que ya trae toda la
    // cadena de variaciones aplicada — el form actualiza s.total en tiempo real
    // al mutar variaciones/asignaciones; el backend guarda el mismo valor).
    // Asi los 5 call sites (form/detalle/lista/mis/cliente-cotizaciones) obtienen
    // la misma estructura sin duplicar el calculo — incluida la separacion de
    // opcionales del subtotal principal y su propio bloque con IGV.
    return {
        ...cotizacion,
        subtotal,
        subtotalExtras,
        igv,
        total,
        subtotalOpcionales,
        igvOpcionales,
        totalOpcionales
    };
}

/**
 * Genera y entrega el PDF de una cotizacion.
 *
 * Firma publica intacta — los 4 call sites no necesitan cambios:
 *   - CotizacionForm.js
 *   - CotizacionesList.js
 *   - MisCotizaciones.js
 *   - ClienteCotizacionesDetalle.js
 *
 * Internamente migramos de jsPDF + jspdf-autotable a @react-pdf/renderer
 * para resolver:
 *   - HTML rich text (TipTap) renderizado correctamente
 *   - Espacios, enlaces, listas, negritas, italicas, headings
 *   - Tipografia Rubik consistente con la app
 *   - Layout multi-pagina automatico
 *   - Texto seleccionable/buscable
 *
 * @param {object} cotizacion - datos de la cotizacion (incluye numero, totales calculados)
 * @param {array} servicios - lista de servicios con sus detalles
 * @param {array} extras - lista de extras
 * @param {object} configuracion - config de la empresa (logo, IGV, metodos de pago, etc.)
 * @param {array} tiposServicio - catalogo completo de tipos de servicio (para agrupar por categoria)
 * @param {boolean} preview - si true abre en nueva pestaña, si false descarga
 */
export async function generatePdfCotizacion({
    cotizacion,
    servicios,
    extras,
    configuracion,
    tiposServicio,
    preview = false
}) {
    // Resolver URL del logo
    const logoConfig = configuracion?.empresa_logo;
    let logoUrl;
    if (logoConfig) {
        if (logoConfig.startsWith('data:') || logoConfig.startsWith('http')) {
            logoUrl = logoConfig;
        } else {
            const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
            logoUrl = `${baseUrl}${logoConfig}`;
        }
    } else {
        logoUrl = logoDefault;
    }

    // Convertir a PNG dataURL — @react-pdf/renderer acepta URLs directas pero
    // rasterizar SVG asegura compatibilidad y evita problemas de CORS.
    const logoDataUrl = await imageToPngDataURL(logoUrl, 200);

    // Enriquecer con totales derivados (opcionales + recalculo de subtotal/IGV/total
    // sin opcionales) — asi TODOS los call sites obtienen la nueva estructura sin
    // duplicar el calculo. Los campos que ya vengan calculados desde el caller
    // (form con variaciones en vivo) mantienen su valor.
    const cotizacionEnriquecida = enrichCotizacionParaPdf(cotizacion, servicios, extras, configuracion);

    // Log de diagnostico comentado. Descomentar si aparece otro bug de layout.
    // console.groupCollapsed('%c[PDF] Data enviada', 'color:#22d3ee;font-weight:bold');
    // console.log('cotizacion:', cotizacionEnriquecida);
    // (servicios || []).forEach((s, i) => console.log(`[${i}] ${s.tipo_servicio_nombre}`, s.detalle));
    // console.groupEnd();

    // Construir el Document react-pdf y generar el blob
    const blob = await pdf(
        <CotizacionPDF
            cotizacion={cotizacionEnriquecida}
            servicios={servicios}
            extras={extras || []}
            configuracion={configuracion}
            tiposServicio={tiposServicio || []}
            logoDataUrl={logoDataUrl}
        />
    ).toBlob();

    // Convencion: COT-<numero>-<cliente>.pdf (ej. COT-MPW-1-Innovid.pdf).
    // Los PDFs generados por sistema NUNCA llevan "-MANUAL" (ese sufijo es
    // exclusivo de archivos subidos manualmente en cotizaciones normales).
    const fileName = nombreArchivoCotizacion(cotizacion);

    if (preview) {
        // Abrir en nueva pestaña para previsualizar
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        // Liberar memoria despues de unos segundos (el browser ya tiene el blob cargado)
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } else {
        // Forzar descarga via anchor temporal
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${fileName}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1_000);
    }
}

export default generatePdfCotizacion;
